import type { FC } from 'react'
import { useState, useEffect } from 'react'

import { Input, LoaderCircle, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useDebounce, getSortedList, getWhiteLabelForDealerStyle } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { countryOptions } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'

import type { DemoCustomers } from '@/api/demoCustomersApi'
import { demoCustomersResources } from '@/api/demoCustomersApi'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Error from '@/components/util/feedback/Error'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'

import { DemoCustomerList } from './DemoCustomerList'
import { DemoCustomerForm } from './Form'

export const SalesSupport: FC = () => {
  const translate = useTranslation()
  const { locale, whiteLabel } = useAppContext()

  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [{ querySort, querySortBy }] = useSortParams()

  const [isOpen, setIsOpen] = useState(false)
  const [errorResponse, setErrorResponse] = useState<string | null>(null)
  const { data: demoCustomers, isLoading: isDemoCustomersLoading } = useQuery(
    queries.demoCustomers(),
    demoCustomersResources.getList,
    {
      onError() {
        setErrorResponse('Could not load your demo customers')
      },
    }
  )

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const stringMatcher = (item: DemoCustomers) => [
    item.name || '',
    item.customerHeadName || '',
    countryOptions.find(country => country.value === item.costObjects[0].countryId)?.label || '',
  ]
  const itemsFiltered = demoCustomers?.filter(getQueryFilter(searchFilter, stringMatcher)) || []
  const sortedList = getSortedList({ list: itemsFiltered, querySort, querySortBy, locale })

  return (
    <Page>
      <h1>
        <Translation id="Sales support" />
      </h1>
      {isDemoCustomersLoading ? (
        <LoaderCircle whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)} />
      ) : (
        <>
          <div className="mb-6">
            <Button
              className="mb-4 ml-0"
              Icon={<PlusIcon />}
              onClick={() => setIsOpen(true)}
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            >
              <Translation id="Create demo customer" />
            </Button>
            <Input
              id="searchFilter"
              type="text"
              value={searchFilter}
              placeholder={translate('Search')}
              onChange={e => setSearchFilter(e.target.value)}
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            />
          </div>

          {errorResponse && <Error textId={errorResponse} />}

          <DemoCustomerList demoCustomers={sortedList} searchFilter={searchFilter} />

          <Popup
            className="w-2/3"
            ariaLabel={translate('Add demo customer')}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          >
            <DemoCustomerForm onClose={() => setIsOpen(false)} />
          </Popup>
        </>
      )}
    </Page>
  )
}
